/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  DeepDiveTemplate,
  type DeepDiveConfig,
} from "@/components/landing/deep-dive-template";
import {
  Upload,
  Layers,
  ZoomIn,
  Monitor,
  FileImage,
  Navigation,
  Ruler,
} from "lucide-react";

const config: DeepDiveConfig = {
  parent: { label: "Takeoffs", href: "/features/takeoffs" },
  badge: "Blueprint Editor",
  title: "Upload the plans.",
  titleHighlight: "Draw on them in your browser.",
  description:
    "Drop in a PDF or image — single page or a full plan set. Navigate sheets, zoom in, and measure directly in your browser without separate desktop software.",

  painPoint: {
    title: "Why most blueprint tools are a pain",
    description:
      "Too many takeoff tools add friction: installs, heavy training, broken plan-set navigation, and manual re-entry into estimating. That slows bids and increases errors.",
    bullets: [
      "No desktop install, no CAD license, no IT department required",
      "Large multi-page PDFs stay usable in a browser workflow",
      "Plan set stays as one document with page navigation, not split into loose files",
      "Measurements draw directly on the canvas and flow into your estimate — no CSV export step",
      "Works on any machine with a browser — field laptop, office desktop, tablet",
    ],
  },

  walkthrough: {
    title: "How the blueprint editor works",
    steps: [
      {
        title: "Upload a PDF or image",
        description:
          "Drag and drop plans into the Takeoff tab of your estimate. Multi-page plan sets stay together as one document so teams can move sheet-to-sheet without losing context.",
        details: [
          "PDF and image formats supported",
          "Multi-page plan sets uploaded as one document",
          "Large-file-friendly upload workflow",
          "Plan set linked to the estimate automatically",
        ],
      },
      {
        title: "Navigate pages and zoom",
        description:
          "Flip between sheets — site plan, floor plan, elevations, details — from one workspace. Zoom and pan are smooth enough for practical production estimating review.",
        details: [
          "Page navigator for multi-page plan sets",
          "Smooth zoom and pan behavior",
          "Readable plan detail at working zoom levels",
          "Keyboard and mouse navigation support",
          "Faster movement through large plan sets",
        ],
      },
      {
        title: "Draw measurements on the canvas",
        description:
          "After calibrating the scale (see Scale Calibration), pick a measurement type — area, linear, count, or volume — and draw directly on the page. Each measurement shows its value in real time as you draw. Measurements are stored as geometry, so you can come back, edit, resize, reassign to a different work area, or delete without starting over.",
        details: [
          "Four measurement types: area (SF/SY), linear (LF), count (EA), volume (CY)",
          "Real-time quantity display as you draw",
          "Stored geometry — always editable after the fact",
          "Measurements sidebar for rename, reassign, edit, delete",
          "Cross-page measurements supported (volume height from one page, area from another)",
        ],
      },
    ],
  },

  details: {
    title: "What makes this blueprint editor different",
    items: [
      {
        icon: Monitor,
        title: "Smooth In-Browser Plan Review",
        description:
          "Review and navigate plans in-browser without desktop-only tooling, even on larger plan sets.",
      },
      {
        icon: Layers,
        title: "Multi-Page Plan Sets",
        description:
          "A plan set is one document. Navigate between sheets with the page navigator. Every page can have its own scale. Measurements persist across pages.",
      },
      {
        icon: Upload,
        title: "Large File Upload",
        description:
          "Upload large blueprint files with a workflow designed for real project documentation.",
      },
      {
        icon: ZoomIn,
        title: "Crisp At Any Zoom",
        description:
          "Keep linework readable while zooming so measurements stay trustworthy.",
      },
      {
        icon: FileImage,
        title: "PDF and Image Support",
        description:
          "Vector PDFs stay sharp at working zoom levels. Scanned PDFs display at the quality of the original scan. Images (PNG, JPG) upload directly.",
      },
      {
        icon: Navigation,
        title: "Full Keyboard & Mouse Controls",
        description:
          "Use familiar keyboard and mouse controls to move through plans quickly during high-volume takeoff sessions.",
      },
    ],
  },

  siblings: [
    {
      label: "Measurement Tool",
      href: "/features/takeoffs/measurement-tool",
      icon: Ruler,
      description:
        "Area, linear, count, volume, scale calibration, quick entry",
    },
    {
      label: "Plan Sets",
      href: "/features/takeoffs/plan-sets",
      icon: Layers,
      description: "Multi-page sets with cross-page measurements",
    },
  ],
};

export function BlueprintEditorPageClient() {
  return <DeepDiveTemplate config={config} />;
}
